import React, { useContext, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import StarekContext from "../../context/StarekContext";
import EfficiencyMonitor from "../../components/EfficiencyMonitor";
import Tile from "../../components/Tile";
import AIAnalysisCard from "../../components/AIAnalysisCard";
import UpcomingMaintenance from "../../components/UpcomingMaintenance";
import FavReportsStrip from "../../components/FavReportsStrip";
import MachineStatusCard from "../../components/MachineStatusCard";
import ShiftsOverview from "../../components/ShiftsOverview";

const GET_DASHBOARD_DATA = gql`
    query GetDashboardMetrics(
        $where: ShadowDeviceWhereInput
        $jobScheduleWhere: JobScheduleWhereInput
        $maintenanceWhere: MaintenanceScheduleWhereInput
        $userWhere: UserWhereInput
        $partWhere: PartWhereInput
    ) {
        aggregateUser(where: $userWhere) {
            _count {
                id
            }
        }
        aggregatePart(where: $partWhere) {
            _count {
                id
            }
        }
        shadowDevices(where: $where) {
            id
            name
            device
            parameters
            jobSchedules {
                id
                name
                start_time
                end_time
                part {
                    id
                    name
                    description
                }
                completed_cycles
                operations {
                    id
                    status
                    planned_duration
                }
            }
        }
        aggregateJobSchedule(where: $jobScheduleWhere) {
            _count {
                id
            }
        }
        aggregateMaintenanceSchedule(where: $maintenanceWhere) {
            _count {
                id
            }
        }
        maintenanceSchedules(where: $maintenanceWhere) {
            id
            name
            description
            schedule_type
            schedule_attributes
            device_id
        }
    }
`;

const GET_SHIFTS = gql`
    query GetShiftsForDashboard($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            start_time
            end_time
            runs_on
        }
    }
`;

const GET_FAV_REPORTS = gql`
    query GetFavReportsForDashboard($where: FavoriteReportsWhereInput) {
        findManyFavoriteReports(where: $where) {
            id
            name
            description
            parameters
        }
    }
`;

const INACTIVITY_SECS = 5 * 60;

function Dashboard() {
    const starekContext = useContext(StarekContext);
    const plantId = starekContext.lastUsedPlant?.id;

    const deviceWhere = {
        plant: { is: { id: { equals: plantId } } },
    };
    const plantIdWhere = { plant_id: { equals: plantId } };

    const { data: dashData, loading: dashLoading } = useQuery(
        GET_DASHBOARD_DATA,
        {
            variables: {
                where: deviceWhere,
                jobScheduleWhere: plantIdWhere,
                maintenanceWhere: plantIdWhere,
                userWhere: { is_active: { equals: true } },
                partWhere: {
                    plant_id: { equals: plantId },
                    is_active: { equals: true },
                },
            },
            skip: !plantId,
        },
    );

    const { data: shiftData, loading: shiftLoading } = useQuery(GET_SHIFTS, {
        variables: { plantId },
        skip: !plantId,
    });

    const { data: favData, loading: favLoading } = useQuery(GET_FAV_REPORTS, {
        variables: { where: plantIdWhere },
        skip: !plantId,
    });

    const devices = dashData?.shadowDevices || [];
    const shifts = shiftData?.shifts || [];
    const favReports = favData?.findManyFavoriteReports || [];
    const maintenanceSchedules = dashData?.maintenanceSchedules || [];

    // Compute online device count using same heartbeat logic as DeviceHealthMonitor
    const { onlineCount, totalCount } = useMemo(() => {
        const total = devices.length;
        const online = devices.filter((d) => {
            const secsAgo = moment().diff(
                moment(d?.device?.lastSeen?.master || 0),
                "seconds",
            );
            return secsAgo < INACTIVITY_SECS;
        }).length;
        return { onlineCount: online, totalCount: total };
    }, [devices]);

    // Shifts scheduled for today (for the tile count)
    const todayShiftsCount = useMemo(() => {
        const todayDow = moment().day();
        return shifts.filter(
            (s) => !s.runs_on || (s.runs_on & (1 << todayDow)) !== 0,
        ).length;
    }, [shifts]);

    const onlineTileType =
        totalCount === 0
            ? "primary"
            : onlineCount / totalCount >= 0.8
              ? "success"
              : onlineCount / totalCount >= 0.5
                ? "warning"
                : "danger";

    return (
        <section className="content">
            <div className="container-fluid">
                {/* ── KPI tiles ─────────────────────────────────────────── */}
                <div className="row mb-2">
                    <Tile
                        colLg={2}
                        type={onlineTileType}
                        icon="fa fa-microchip"
                        header={
                            dashLoading ? "…" : `${onlineCount}/${totalCount}`
                        }
                        description="Fleet Online"
                        linkText="Manage Devices"
                        linkNavigateTo="/devices"
                    />
                    <Tile
                        colLg={2}
                        type="info"
                        icon="fa fa-cube"
                        header={
                            dashLoading
                                ? "…"
                                : String(
                                      dashData?.aggregatePart._count.id ?? "",
                                  )
                        }
                        description="Parts"
                        linkText="Manage Parts"
                        linkNavigateTo="/parts"
                    />
                    <Tile
                        colLg={2}
                        type="warning"
                        icon="fa fa-cogs"
                        header={
                            dashLoading
                                ? "…"
                                : String(
                                      dashData?.aggregateMaintenanceSchedule
                                          ._count.id ?? "",
                                  )
                        }
                        description="Maintenance"
                        linkText="View Schedule"
                        linkNavigateTo="/maintenance"
                    />
                    <Tile
                        colLg={2}
                        type="success"
                        icon="fa fa-calendar"
                        header={
                            dashLoading
                                ? "…"
                                : String(
                                      dashData?.aggregateJobSchedule._count
                                          .id ?? "",
                                  )
                        }
                        description="Job Schedules"
                        linkText="Manage Schedules"
                        linkNavigateTo="/job-schedules"
                    />
                    <Tile
                        colLg={2}
                        type="primary"
                        icon="fa fa-users"
                        header={
                            dashLoading
                                ? "…"
                                : String(
                                      dashData?.aggregateUser._count.id ?? "",
                                  )
                        }
                        description="Users"
                        linkText="Manage Users"
                        linkNavigateTo="/users"
                    />
                    <Tile
                        colLg={2}
                        type="info"
                        icon="fa fa-clock-o"
                        header={dashLoading ? "…" : String(todayShiftsCount)}
                        description="Shifts Today"
                        linkText="Manage Shifts"
                        linkNavigateTo="/shifts"
                    />
                </div>

                {/* ── Live Insights (AI) ───────────────────────────────── */}
                <div className="row mb-3">
                    <div className="col-12">
                        <AIAnalysisCard
                            deviceData={dashData}
                            maintenanceSchedules={maintenanceSchedules}
                            shifts={shifts}
                            loading={dashLoading || shiftLoading}
                        />
                    </div>
                </div>

                {/* ── Snapshots Strip ────────────────────────────────────── */}
                <div className="row mb-3">
                    <div className="col-12">
                        <FavReportsStrip
                            favReports={favReports}
                            loading={favLoading}
                        />
                    </div>
                </div>

                {/* ── Live Production Monitor + Upcoming Maintenance ────── */}
                <div className="row mb-3">
                    <section className="col-lg-7 connectedSortable ui-sortable">
                        <EfficiencyMonitor deviceData={dashData} />
                    </section>
                    <section className="col-lg-5 connectedSortable ui-sortable">
                        <UpcomingMaintenance
                            maintenanceSchedules={maintenanceSchedules}
                            loading={dashLoading}
                        />
                    </section>
                </div>

                {/* ── Fleet Overview (machine status + health pie) ──────── */}
                <div className="row mb-3">
                    <div className="col-12">
                        <MachineStatusCard
                            devices={devices}
                            maintenanceSchedules={maintenanceSchedules}
                            loading={dashLoading}
                        />
                    </div>
                </div>

                {/* ── Shifts Overview ───────────────────────────────────── */}
                <div className="row mb-3">
                    <div className="col-12">
                        <ShiftsOverview
                            devices={devices}
                            shifts={shifts}
                            loading={dashLoading || shiftLoading}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Dashboard;
